// ============================================================================
// Users & Roles — mock data
// ----------------------------------------------------------------------------
// Re-skinned from the OMOP DataHub "Users & Roles" reference into FederateX
// conventions (realm: `federatex`, federated-learning role names, client-site
// project scopes). All shapes mirror what the BFF returns — see
// docs/users-roles-api.md. Every consuming component marks the real call with a
// `// BFF call:` comment so wiring later is a search-and-replace.
// ============================================================================

export const KEYCLOAK_REALM = "federatex";

// Monospace stack for keys / UUIDs / timestamps (matches theme.typography.fontFamilyMono)
export const MONO_FONT =
  '"JetBrains Mono", "SF Mono", "Roboto Mono", ui-monospace, monospace';

// Deterministic avatar palette (navy-friendly, accessible on white)
export const AVATAR_COLORS = [
  "#1B365D", // navy (primary)
  "#0087B4", // cyan (secondary)
  "#16a34a", // green
  "#d97706", // amber
  "#7c3aed", // purple
  "#0e7490", // teal
  "#be123c", // rose
  "#4f46e5", // indigo
];

// ----------------------------------------------------------------------------
// Role definitions (joined with GET /api/roles at runtime in production)
// ----------------------------------------------------------------------------
export const ROLE_META = {
  fl_engineer: { label: "FL Engineer", color: "#16a34a", pill: "green" },
  fl_analyst: { label: "FL Analyst", color: "#0087B4", pill: "cyan" },
  site_operator: { label: "Site Operator", color: "#d97706", pill: "amber" },
  platform_admin: { label: "Platform Admin", color: "#7c3aed", pill: "purple" },
};

export const ROLE_FILTERS = [
  { key: "all", label: "All" },
  { key: "fl_engineer", label: "FL Engineer" },
  { key: "fl_analyst", label: "FL Analyst" },
  { key: "site_operator", label: "Site Operator" },
  { key: "platform_admin", label: "Admin" },
];

// Project / client-site scopes (maps to the `site_ids` JWT claim)
export const PROJECT_SCOPES = ["MUHC", "CHUM", "SickKids", "JGH"];

// ----------------------------------------------------------------------------
// Members  (GET /api/users  +  per-user GET /api/users/{id}/roles)
// ----------------------------------------------------------------------------
export const USERS = [
  {
    id: "8f3c1a20-0001-4a11-9c01-aa0000000001",
    initials: "AD",
    name: "A. Diallo",
    email: "a.diallo@federatex.io",
    roles: ["platform_admin"],
    projects: [],
    enabled: true,
    federated: false, // false → "Keycloak" badge, true → "LDAP" badge
    since: "2024-08-15",
    color: 0,
  },
  {
    id: "8f3c1a20-0002-4a11-9c01-aa0000000002",
    initials: "JL",
    name: "J. Lambert",
    email: "j.lambert@federatex.io",
    roles: ["fl_engineer"],
    projects: ["MUHC", "CHUM"],
    enabled: true,
    federated: false,
    since: "2024-09-01",
    color: 1,
  },
  {
    id: "8f3c1a20-0003-4a11-9c01-aa0000000003",
    initials: "SR",
    name: "S. Roy",
    email: "s.roy@federatex.io",
    roles: ["fl_engineer"],
    projects: ["MUHC"],
    enabled: true,
    federated: false,
    since: "2024-10-15",
    color: 2,
  },
  {
    id: "8f3c1a20-0004-4a11-9c01-aa0000000004",
    initials: "AP",
    name: "A. Patel",
    email: "a.patel@federatex.io",
    roles: ["fl_analyst"],
    projects: ["MUHC", "CHUM"],
    enabled: true,
    federated: false,
    since: "2024-11-01",
    color: 3,
  },
  {
    id: "8f3c1a20-0005-4a11-9c01-aa0000000005",
    initials: "LC",
    name: "L. Chen",
    email: "l.chen@federatex.io",
    roles: ["fl_analyst"],
    projects: ["MUHC"],
    enabled: true,
    federated: false,
    since: "2025-01-02",
    color: 4,
  },
  {
    id: "8f3c1a20-0006-4a11-9c01-aa0000000006",
    initials: "MB",
    name: "M. Beauchamp",
    email: "m.beauchamp@muhc.ca",
    roles: ["site_operator"],
    projects: ["MUHC"],
    enabled: true,
    federated: true,
    since: "2024-12-01",
    color: 5,
  },
  {
    id: "8f3c1a20-0007-4a11-9c01-aa0000000007",
    initials: "FT",
    name: "F. Tremblay",
    email: "f.tremblay@muhc.ca",
    roles: ["site_operator"],
    projects: ["MUHC"],
    enabled: true,
    federated: true,
    since: "2024-12-01",
    color: 6,
  },
];

// ----------------------------------------------------------------------------
// Role cards  (permission matrix is frontend metadata, joined with live roles)
// ----------------------------------------------------------------------------
export const ROLE_DEFS = [
  {
    key: "fl_engineer",
    desc:
      "Full platform access. Can register datasets and models, configure training, " +
      "launch federated runs across client sites, and review all results and audit logs.",
    perms: {
      "DataHub": ["Register datasets", "Edit data contracts", "View profiling"],
      "Learning Models": ["Register models", "Edit model configs"],
      "Training": ["Configure training setup", "Launch federated runs", "Pause / resume runs"],
      "Results": ["View all run outputs", "Export results"],
      Platform: ["View audit log", "Manage versioning"],
    },
    denied: ["Manage users / roles"],
  },
  {
    key: "fl_analyst",
    desc:
      "Reviews federated outputs and approves model candidates. Cannot launch runs or " +
      "modify platform configuration.",
    perms: {
      DataHub: ["View profiling"],
      Training: ["View run progress"],
      Results: ["View run outputs", "Approve / reject candidates", "View metrics"],
    },
    denied: ["Launch any run", "Edit configs", "Export results", "Audit log"],
  },
  {
    key: "site_operator",
    desc:
      "Client-site operator. Site-scoped access only, can view runs and outputs for their " +
      "assigned sites. ABAC enforced on the `site_ids` JWT claim.",
    perms: {
      "Site runs": ["View runs for own site", "View local data status"],
      "Site results": ["View QA report", "Export own-site outputs"],
    },
    denied: ["Cross-site access", "Approve / reject candidates", "Launch any run"],
    note: "Site scope is set via the `site_ids` claim in Keycloak user attributes.",
  },
  {
    key: "platform_admin",
    desc:
      "Full platform administration. All permissions including user management, role " +
      "assignment, and audit access.",
    perms: {
      "All platform areas": ["All permissions"],
      Platform: [
        "Manage users / roles",
        "View audit log",
        "Manage versioning",
        "Realm configuration",
      ],
    },
    denied: [],
  },
];

// ----------------------------------------------------------------------------
// Pending invitations  (GET /api/users?enabled=false)
// ----------------------------------------------------------------------------
export const PENDING_INVITES = [
  {
    id: "pending-0001",
    email: "m.tremblay@muhc.ca",
    role: "site_operator",
    projects: ["MUHC"],
    sent: "2026-06-07",
    expiresInH: 6,
  },
  {
    id: "pending-0002",
    email: "p.chen@federatex.io",
    role: "fl_analyst",
    projects: ["MUHC", "CHUM"],
    sent: "2026-06-06",
    expiresInH: 18,
  },
];

// ----------------------------------------------------------------------------
// Activity log  (normalized from GET /api/events + GET /api/admin-events)
// kind: invite | login | edit | remove
// ----------------------------------------------------------------------------
export const ACTIVITY_LOG = [
  {
    id: "a1",
    kind: "invite",
    severity: "green",
    actor: "A. Diallo",
    message: "invited <strong>p.chen@federatex.io</strong> with role fl_analyst",
    ts: "2026-06-06 14:22",
  },
  {
    id: "a2",
    kind: "login",
    severity: "cyan",
    actor: "J. Lambert",
    message: "logged in via Keycloak OIDC (PKCE)",
    ts: "2026-06-08 09:38",
  },
  {
    id: "a3",
    kind: "invite",
    severity: "green",
    actor: "A. Diallo",
    message:
      "invited <strong>m.tremblay@muhc.ca</strong> with role site_operator · site MUHC",
    ts: "2026-06-07 11:05",
  },
  {
    id: "a4",
    kind: "edit",
    severity: "amber",
    actor: "A. Diallo",
    message: "changed <strong>S. Roy</strong> site scope from MUHC to MUHC, CHUM",
    ts: "2026-06-05 16:42",
  },
  {
    id: "a5",
    kind: "login",
    severity: "cyan",
    actor: "M. Beauchamp",
    message: "logged in via Keycloak LDAP federation",
    ts: "2026-06-05 09:12",
  },
  {
    id: "a6",
    kind: "login",
    severity: "cyan",
    actor: "F. Tremblay",
    message: "logged in via Keycloak LDAP federation",
    ts: "2026-06-05 09:09",
  },
  {
    id: "a7",
    kind: "edit",
    severity: "amber",
    actor: "A. Diallo",
    message: "assigned role <strong>platform_admin</strong> to A. Diallo",
    ts: "2025-12-20 10:00",
  },
  {
    id: "a8",
    kind: "remove",
    severity: "red",
    actor: "A. Diallo",
    message: "removed <strong>B. Smith</strong> from the realm, access revoked",
    ts: "2025-12-15 15:30",
  },
  {
    id: "a9",
    kind: "invite",
    severity: "green",
    actor: "A. Diallo",
    message: "created user <strong>L. Chen</strong> with role fl_analyst",
    ts: "2025-01-02 09:55",
  },
];

// ----------------------------------------------------------------------------
// Active sessions  (GET /api/users/{id}/sessions)
// ----------------------------------------------------------------------------
export const ACTIVE_SESSIONS = [
  { id: "s1", user: "A. Diallo", ip: "10.4.2.18", client: "federatex-web", lastAccess: "just now" },
  { id: "s2", user: "J. Lambert", ip: "10.4.2.51", client: "federatex-web", lastAccess: "3 min ago" },
  { id: "s3", user: "M. Beauchamp", ip: "172.16.8.4", client: "federatex-web", lastAccess: "21 min ago" },
];

// ----------------------------------------------------------------------------
// Integration Flows — BFF route map (static reference rendered in-app)
// ----------------------------------------------------------------------------
export const BFF_ROUTE_MAP = [
  { tab: "Members", action: "Load member grid", bff: "GET /api/users", kc: "GET /admin/realms/{realm}/users" },
  { tab: "Members", action: "Role pills per user", bff: "GET /api/users/{id}/roles", kc: "GET …/users/{id}/role-mappings/realm" },
  { tab: "Members", action: "Filter-button counts", bff: "GET /api/roles/{role}/users", kc: "GET …/roles/{role}/users" },
  { tab: "Members", action: "Save user edits", bff: "PUT /api/users/{id}", kc: "PUT …/users/{id}" },
  { tab: "Members", action: "Remove user", bff: "DELETE /api/users/{id}", kc: "DELETE …/users/{id}" },
  { tab: "Members", action: "Force re-login", bff: "DELETE /api/users/{id}/sessions", kc: "DELETE …/users/{id}/sessions" },
  { tab: "Invite", action: "① Create user", bff: "POST /api/users", kc: "POST …/users → Location header" },
  { tab: "Invite", action: "② Assign role", bff: "POST /api/users/{id}/roles", kc: "POST …/role-mappings/realm" },
  { tab: "Invite", action: "③ Send setup email", bff: "PUT /api/users/{id}/invite-email", kc: "PUT …/execute-actions-email" },
  { tab: "Invite", action: "Pending invitations", bff: "GET /api/users?enabled=false", kc: "GET …/users?enabled=false" },
  { tab: "Invite", action: "Resend invitation", bff: "PUT /api/users/{id}/invite-email", kc: "PUT …/execute-actions-email" },
  { tab: "Roles", action: "List roles", bff: "GET /api/roles", kc: "GET …/roles" },
  { tab: "Roles", action: "Resolve role UUID", bff: "GET /api/roles/{role}", kc: "GET …/roles/{role}" },
  { tab: "Roles", action: "Members of a role", bff: "GET /api/roles/{role}/users", kc: "GET …/roles/{role}/users" },
  { tab: "Roles", action: "Grant role", bff: "POST /api/users/{id}/roles", kc: "POST …/role-mappings/realm" },
  { tab: "Roles", action: "Revoke role", bff: "DELETE /api/users/{id}/roles", kc: "DELETE …/role-mappings/realm" },
  { tab: "Activity", action: "Login / logout", bff: "GET /api/events", kc: "GET …/events" },
  { tab: "Activity", action: "Admin operations", bff: "GET /api/admin-events", kc: "GET …/admin-events" },
  { tab: "Activity", action: "Active sessions", bff: "GET /api/users/{id}/sessions", kc: "GET …/users/{id}/sessions" },
];

// Helper: role metadata with a safe fallback for unknown role keys
export const getRoleMeta = (key) =>
  ROLE_META[key] || { label: key, color: "#64748b", pill: "gray" };
